<template>
  <div class="col pb-4">
    <div
      class="card mb-4 rounded-3 shadow-sm h-100"
      :class="{ 'border-primary': isHighlighted }"
    >
      <!-- Header -->
      <div
        class="card-header py-3"
        :class="{ 'text-bg-primary border-primary text-white': isHighlighted }"
      >
        <h4 class="my-0 fw-normal">{{ title }}</h4>
      </div>

      <!-- Body -->
      <div class="card-body d-flex flex-column">
        <div class="text-muted pb-3">{{ subtitle }}</div>

        <!-- Price -->
        <h1 class="card-title pricing-card-title pb-1">
          <span v-if="title === 'Audio Basic'">ab </span>{{ price }}€
          <small class="text-muted fw-light">/Episode</small>
        </h1>

        <!-- Features -->
        <div
          class="text-start px-3"
          v-for="(feature, index) in features"
          :key="index"
        >
          <div class="d-flex py-1">
            <div
              class="d-flex align-items-center"
              style="width: 28px; flex-shrink: 0"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style="min-width: 20px; min-height: 20px"
              >
                <path
                  d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div class="ps-2">{{ feature }}</div>
          </div>
        </div>

        <!-- Show calculator only if showCalculator is true -->
        <Calculator
          v-if="showCalculator"
          :package-type="packageType"
          @update:modelValue="(data) => handlePriceUpdate(data)"
        />

        <!-- Audio Player -->
        <div v-if="showAudioPlayer" class="py-3">
          <div
            v-if="title === 'Audio Basic'"
            class="audio-prompt-wrapper text-center my-3"
          >
            <span class="fw-bold audio-prompt-text">
              Hör jetzt rein und überzeug dich selbst
            </span>
          </div>
          <AudioPlayer
            :src="masteredAudioFile"
            :original-src="originalAudioFile"
          />
        </div>

        <!-- Bullet Points -->
        <div v-if="bulletPoints.length > 0">
          <h5 class="text-black pt-3">Leistungen</h5>
          <ul class="text-start px-3 list-unstyled">
            <li
              v-for="(point, index) in bulletPoints"
              :key="index"
              class="d-flex py-1"
            >
              <div
                class="pe-2 fw-bold"
                style="font-size: 1.3em; line-height: 1em"
              >
                •
              </div>
              <div>{{ point }}</div>
            </li>
          </ul>
        </div>

        <!-- Footer -->
        <div class="d-flex row h-100 align-content-end pb-3 px-3">
          <span class="text-end text-muted">{{ note }}</span>
        </div>

        <!-- CTA Button -->
        <a
          :id="buttonId"
          class="mt-auto w-100 btn btn-lg hover:pointer"
          :class="
            isHighlighted
              ? 'btn-primary'
              : 'btn-outline-primary audio-secondary-btn'
          "
          :href="ctaLink"
          target="_blank"
        >
          {{ ctaText }}
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    required: true,
  },
  price: {
    type: [Number, String],
    required: true,
  },
  features: {
    type: Array as () => string[],
    default: () => [],
  },
  bulletPoints: {
    type: Array as () => string[],
    default: () => [],
  },
  ctaText: {
    type: String,
    required: true,
  },
  ctaLink: {
    type: String,
    required: true,
  },
  buttonId: {
    type: String,
    required: true,
  },
  isHighlighted: {
    type: Boolean,
    default: false,
  },
  showCalculator: {
    type: Boolean,
    default: false,
  },
  showAudioPlayer: {
    type: Boolean,
    default: false,
  },
  packageType: {
    type: String,
    default: "",
  },
  masteredAudioFile: {
    type: String,
    default: "",
  },
  originalAudioFile: {
    type: String,
    default: "",
  },
  note: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["priceUpdate"]);

const handlePriceUpdate = (data: {
  numberOfSpeakers: number;
  episodeDuration: number;
  price: number;
}) => {
  emit("priceUpdate", data);
};
</script>

<style scoped>
.audio-prompt-text {
  position: relative;
  display: inline-block;
  padding-right: 28px; /* Space for the ear icon */
}

.ear-icon {
  position: absolute;
  width: 24px;
  height: 24px;
  right: 0;
  bottom: -5px;
}

/* Add hover styles for CTA button */
.btn {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.btn-primary:hover,
.btn-outline-primary:hover {
  color: white !important;
}

@media (max-width: 576px) {
  .audio-prompt-text {
    padding-right: 24px;
  }

  .ear-icon {
    bottom: -3px;
  }
}
</style>
